import { type RefObject } from 'react'
import { TooltipProvider } from '@teach/ui'
import type { Lesson } from '@/types/lesson'
import type { ContentLang } from '@/features/editor/editorSlice'
import { BlockStrip } from './BlockStrip'
import { useLessonBlocksActions } from './useLessonBlocksActions'
import { blockLeftOf } from '@/features/editor/lib/blockLeftOf'
import { liveIdentity } from '@/features/moderation/lib/liveIdentity'
import type { BlocksInRun } from './useBlocksInRun'
import { isSameBlock } from './blockDraftSync'
import { useAppSelector } from '@/store/hooks'

interface Props {
  lesson: Omit<Lesson, 'blocks'>
  /** §23: прокидывается в `useLessonBlocksActions` ради инвалидации `Course`. */
  courseId: number
  contentLang: ContentLang
  selectedBlockId: number | null
  selectedRef: RefObject<HTMLDivElement>
  isCourseAuthor: boolean
  /** Блоки, занятые прогоном, пишущим в блоки (`useBlocksInRun`). */
  blocksInRun: BlocksInRun
  /**
   * Любой прогон, пишущий в блоки, активен (`useIsBlockWritingRunActive`) —
   * гейт реордера. Опционален ради старых тестов этого компонента.
   */
  isBlockWritingRunActive?: boolean
  /** Курс на проверке (`useCourseTimeline.isFrozen`). ОБЯЗАТЕЛЕН намеренно:
   *  забытый проп молча оставил бы урок открытым для записи, которую бэк
   *  отобьёт. */
  isFrozen: boolean
  onSelectBlock: (blockId: number) => void
  /** `next` — блок слева от удалённого (`blockLeftOf`), его выделят вместо него. */
  onDeleteBlock: (blockId: number, next: number | null) => void
  onBlockCreated: (blockId: number) => void
  /** п.10: набор мультивыбора — опционален по тому же образцу, что и
   *  `isBlockWritingRunActive` выше (ради старых тестов этого компонента). */
  selectedBlockIds?: number[]
  onToggleSelectBlock?: (blockId: number) => void
  /** Свёрнутая лента: плашки вместо миниатюр (`useTimelineCollapsed`). */
  compact?: boolean
}

export const LessonExpanded = ({
  lesson,
  courseId,
  contentLang,
  selectedBlockId,
  selectedRef,
  isCourseAuthor,
  blocksInRun,
  isBlockWritingRunActive = false,
  isFrozen,
  onSelectBlock,
  onDeleteBlock,
  onBlockCreated,
  selectedBlockIds = [],
  onToggleSelectBlock = () => {},
  compact = false,
}: Props) => {
  const {
    previewFor,
    blocks,
    isLoading,
    isCreating,
    isSourceSettling,
    popoverOpen,
    setPopoverOpen,
    handleAdd,
    handleDuplicate,
    handleReorder,
  } = useLessonBlocksActions({
    lessonId: lesson.id,
    lessonIdentity: liveIdentity(lesson),
    courseId,
    onBlockCreated,
    blocksInRun,
    isBlockWritingRunActive,
    lessonTheme: lesson.theme,
    isFrozen,
  })
  // Список урока может отставать от выделения (первая правка живого курса уже
  // перевела его на id черновика) — миниатюра подсвечивается через алиасы.
  const idAliases = useAppSelector((s) => s.editor.idAliases)
  const stripSelectedId =
    blocks.find((b) => isSameBlock(idAliases, b.id, selectedBlockId))?.id ?? selectedBlockId

  return (
    <TooltipProvider delayDuration={200}>
      {/* Ref parity: the expanded lesson's blocks sit INLINE next to the lesson
          square — no bordered card, no name/type header (those move to the
          right panel), sized to content (the footer scrolls). */}
      <div className="flex h-full items-stretch">
        <BlockStrip
          blocks={blocks}
          isLoading={isLoading}
          isCreating={isCreating}
          selectedBlockId={stripSelectedId}
          selectedRef={selectedRef}
          contentLang={contentLang}
          isCourseAuthor={isCourseAuthor}
          blocksInRun={blocksInRun}
          isFrozen={isFrozen}
          isSourceSettling={isSourceSettling}
          popoverOpen={popoverOpen}
          onPopoverChange={setPopoverOpen}
          onSelectBlock={onSelectBlock}
          onDeleteBlock={(id) => onDeleteBlock(id, blockLeftOf(blocks, id))}
          selectedBlockIds={selectedBlockIds}
          onToggleSelectBlock={onToggleSelectBlock}
          onAdd={(card) => void handleAdd(card)}
          previewFor={previewFor}
          onDuplicate={handleDuplicate}
          onReorder={(from, to) => void handleReorder(from, to)}
          lessonType={lesson.type}
          compact={compact}
        />
      </div>
    </TooltipProvider>
  )
}
